import {
	database,
} from 'utils/database';


import {
	useBasisService,
} from 'services';
import {
	Image,
	DeviationModel,
} from 'models';
import {
	toastError,
} from 'utils/toast';
import {
	getErrorMessageFromStatus,
} from 'utils/error';



export const useDeviationService = () => { // Model
	const {
		data,
		http,
		loading: [isLoading, setIsLoading],
		error: [error, setError],
		table,
	} = useBasisService<DeviationModel>(database.deviations);

	const getPath = (transportId: number): string =>
		`api/transport/${transportId}/deviations`;

	const getByTransportId = (id: number): Promise<DeviationModel[] |undefined> => {
		setIsLoading(true);
		setError(null);
		// table.clear(); where id == transportId

		return new Promise((resolve, reject) => {
			http.get<DeviationModel[]>(
				`api/transport/${id}/deviations`, {
					table,
				},
				() => table.where('transportId').equals(id)
			)
				.then((deviations) => {
					setIsLoading(false);
					resolve(deviations || [])
				})
				.catch(error => {
					setIsLoading(false);
					setError(error);
					toastError('Kunne ikke hente avvik', getErrorMessageFromStatus(error.status));
					reject(error);
				});
		});
	};

	const upsert = (deviation: DeviationModel): Promise<void> => {
		setIsLoading(true);
		setError(null);
		return new Promise(async (resolve, reject) => {
			if (deviation.transportId === undefined) {
				setError('Tur ID mangler.');
				reject('Missing trip id');
				return;
			}
			const path = getPath(deviation.transportId);
			(deviation.id ? http.patch : http.post)(
				deviation.id ? `${path}/${deviation.id}` : path,
				{
					...deviation,
					timestamp: new Date(deviation.timestamp).toJSON(),
				},
				{
					table,
				},
				false,
			)
				.then(() => {
					setIsLoading(false);
					resolve();
				})
				.catch(error => {
					const imagesIsPlural = deviation.images.length > 1;
					setIsLoading(false);
					setError(error);
					toastError(
						`Kunne ikke ${deviation.id ? 'oppdatere' : 'opprette'} avvik`,
						getErrorMessageFromStatus(
							error.status,
							imagesIsPlural ? 'Ett eller flere bilder' : 'Bildet'
						)
					);
					reject(error);
				});
		});
	};

	const getImages = (deviation: DeviationModel): Promise<Image[]> => {
		/**
		 * Once the backend for it is implemented, change it to:
		 * const path = `${getPath(deviation.transportId)}/${deviation.id}/images`;
		 */
		const path = `${getPath(deviation.transportId)}/${deviation.id}?include-images=true`;

		return new Promise<Image[]>((resolve, reject) => {
			http.get<DeviationModel>(path)
				.then((dev: DeviationModel | undefined) => {
					const images = dev?.images || [];
					table.update(deviation.id, {
						images,
					});
					resolve(images || []);
				})
				.catch(error => {
					console.error(error);
					toastError('Kunne ikke hente bilder', getErrorMessageFromStatus(error.status));
					reject(error);
				});
		});
	};

	const postImage = (deviation: DeviationModel, image: Image) => {
		const path = `${getPath(deviation.transportId)}/${deviation.id}/images`;
		return new Promise((resolve, reject) => {
			// TODO: More work on this!
			http.post(path, image)
				.then(resolve)
				.catch(reject);
		});
	};

	const postImages = (deviation: DeviationModel, images: Image[]) => {
		const newImages = images.filter(image => !image.id);
		setIsLoading(true);

		return new Promise((resolve, reject) => {
			Promise.all(
				newImages
					.map(image => postImage(deviation, image))
			)
				.then(result => {
					setIsLoading(false);
					resolve(result);
				})
				.catch(error => {
					const isPlural = !!images.length;

					toastError(`Kunne ikke laste opp bilde${isPlural ? 'ne' : 't'}`, getErrorMessageFromStatus(error.status));
					setIsLoading(false);
					reject(error);
				});
		});
	};

	const removeImage = (deviation: DeviationModel, imageId: number): Promise<void> => {
		const path = `${getPath(deviation.transportId)}/${deviation.id}/images`;
		return new Promise((resolve, reject) => {
			table.delete(imageId)
				.then(() => {
					http.delete<void>(path, imageId)
						.then(resolve)
						.catch(reject);
				})
				.catch(error => {
					console.error(error);
					reject(error);
				});
		});
	};

	const removeImages = (deviation: DeviationModel, images: Image[]) => {
		// Making sure we only try to delete images that are stored in the DB
		const promises: Promise<void>[] = [];
		images.forEach(image => {
			if (image.id) {
				promises.push(removeImage(deviation, image.id))
			}
		});
		return Promise.all(promises)
			.catch(error => {
				toastError('Kunne ikke fjerne bilde', getErrorMessageFromStatus(error.status));
			});
	}

	const remove = (
		transportId?: number | undefined | null,
		deviationId?: number | undefined | null
	): Promise<void> => {
		const errorTitle = 'Kunne ikke fjerne avviket';
		setIsLoading(true);
		setError(null);
		return new Promise(async (resolve, reject) => {
			if (transportId === undefined || transportId == null) {
				setError('TransportID mangler.');
				toastError(errorTitle, 'TransportID mangler.');
				reject('Missing deviation id');
				return;
			}
			if (deviationId === undefined || deviationId == null) {
				setError('AvviksID mangler.');
				toastError(errorTitle, 'AvviksID mangler.');
				reject('Missing deviation id');
				return;
			}

			http.delete(
				getPath(transportId),
				deviationId,
				{
					table,
				}
			)
				.then(() => {
					setIsLoading(false);
					resolve();
				})
				.catch(error => {
					setIsLoading(false);
					setError(error);
					toastError(errorTitle, getErrorMessageFromStatus(error.status));
					reject(error);
				});
		});
	};

	return {
		table,
		data,
		isLoading,
		error,
		getByTransportId,
		getImages,
		upsert,
		postImages,
		removeImages,
		remove,
	};
};